import {
  BaseRequiredDialogProps,
  Button,
  LoadingSkeleton,
} from '@bloomfieldrobotics/canopy';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  Fade,
  IconButton,
  IconButtonProps,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { memo, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../../../../config';
import {
  MapControl,
  MapControlContent,
  MapControlFooter,
  MapControlSection,
} from '../../../../commonUI/maps/controls';
import { PointIdCopyControl } from '../../../../commonUI/maps/controls/PointIdCopyControl';
import { PointImage } from '../../../../commonUI/maps/controls/PointImage/PointImage';
import { Point } from '../../../../commonUI/maps/types';
import { DiagnosticFeature } from '../../../../commonUI/viewMode/DiagnosticFeature';

type Props = PropsWithChildren<
  {
    point: Pick<Point, 'leftCameraImgPath' | 'id'> | undefined;
    onNextPoint: () => void;
    onPreviousPoint: () => void;
    positions?: Partial<{
      top: number;
      right: number;
      left: number;
      bottom: number;
    }>;
  } & BaseRequiredDialogProps
>;

/**
 *
 *
 * Note: This component is wrapped in memo for performance improvements related to Mapbox.
 * Mapbox re-renders all of its children when you hover over any layer.
 * The controls are children of the Map component and we're being forcibly
 * re-rendered when they shouldn't.
 */
export const CollectionPointDetailsControl = memo(
  ({
    open: isOpen,
    onClose,
    point,
    onNextPoint,
    onPreviousPoint,
    children,
    positions = { right: 8, top: 8 },
  }: Props) => {
    const { t } = useTranslation();

    const imageUrl = useMemo<string | undefined>(() => {
      if (point) {
        const imgUrl = new URL(
          point.leftCameraImgPath.replace(RegExp('s3://'), config.api.imageUrl)
        );

        imgUrl.searchParams.append('format', 'jpg');

        return imgUrl.toString();
      }
    }, [point]);

    const pointActions = (
      <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
        <DiagnosticFeature>
          <LoadingSkeleton loading={!point} width={80} height={16}>
            <PointIdCopyControl uniqueId={point?.id ?? ''} />
          </LoadingSkeleton>
        </DiagnosticFeature>
        <Stack direction='row' spacing={1}>
          <ArrowButton size='large' onClick={onPreviousPoint}>
            <ArrowBack fontSize='inherit' />
          </ArrowButton>
          <ArrowButton onClick={onNextPoint}>
            <ArrowForward fontSize='inherit' />
          </ArrowButton>
        </Stack>
      </Stack>
    );

    const footerActions = (
      <Button variant='contained' onClick={onClose}>
        {t('Close')}
      </Button>
    );

    return (
      <Fade appear={false} in={isOpen}>
        <MapControl
          aria-label='Point Metadata'
          tabIndex={-1}
          sx={{
            position: 'absolute',
            ...positions,
            width: 350,
          }}
        >
          <MapControlContent>
            <MapControlSection
              title={t('Point selected')}
              actions={pointActions}
            >
              {point ? (
                <PointImage src={imageUrl} enableColorCorrection />
              ) : (
                <Typography>Unable to display Point image</Typography>
              )}
            </MapControlSection>
            <MapControlSection>{children}</MapControlSection>
          </MapControlContent>

          <MapControlFooter actions={footerActions} />
        </MapControl>
      </Fade>
    );
  }
);

const ArrowButton = styled((props: IconButtonProps) => {
  return <IconButton {...props} size='small'></IconButton>;
})();
